import React, { useState, useEffect } from 'react';
import { Loader2, UserPlus, Power, PowerOff, Lock, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import apiClient from '@/lib/apiClient';

export default function BenutzerTab({ isDark, userInfo, selectedMunicipalityId, municipalitiesList = [] }) {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({ 
    email: '', full_name: '', role: 'Employee', municipality_id: '' 
  });

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const params = {};
      if (userInfo?.role === 'ProviderAdmin' && selectedMunicipalityId) {
        params.municipality_id = selectedMunicipalityId;
      }
      const response = await apiClient.get('/users/', { params });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.response?.status !== 403) {
        toast.error('Fehler beim Laden der Benutzer');
      }
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (userInfo && (userInfo.role === 'ProviderAdmin' || userInfo.role === 'MunicipalityAdmin')) {
      fetchUsers();
    }
  }, [userInfo?.role, selectedMunicipalityId]);

  const handleInviteUser = async (e) => {
    e.preventDefault();
    
    if (userInfo?.role === 'ProviderAdmin' && createUserForm.role !== 'ProviderAdmin') {
      if (!createUserForm.municipality_id) {
        toast.error('Bitte wählen Sie eine Gemeinde für diesen Benutzer aus.', {
          duration: 5000,
        });
        return;
      }
    }
    
    setCreatingUser(true);
    try {
      const payload = {
        email: createUserForm.email,
        full_name: createUserForm.full_name,
        role: createUserForm.role,
        municipality_id: userInfo?.role === 'MunicipalityAdmin' ? userInfo.municipality_id : createUserForm.municipality_id || null
      };
      
      const response = await apiClient.post('/auth/admin-invite', payload);
      
      if (response.data.email_sent) {
        toast.success('Einladung gesendet. Der Nutzer erhält eine E-Mail von login@stadtki.com.', {
          duration: 6000,
        });
      } else {
        toast.warning(
          'Benutzer erstellt, aber E-Mail konnte nicht gesendet werden. Bitte Link manuell teilen.',
          { duration: 8000 }
        );
        console.info('Reset link for manual sharing:', response.data.reset_link);
      }
      
      setCreateUserOpen(false);
      setCreateUserForm({ email: '', full_name: '', role: 'Employee', municipality_id: '' });
      fetchUsers();
    } catch (error) {
      console.error('Invite User Error:', error);
      const status = error.response?.status;
      const errorDetail = error.response?.data?.detail || error.message;
      
      if (status === 401 || status === 403) {
        toast.error('Nicht berechtigt oder Sitzung abgelaufen. Bitte erneut einloggen.', { duration: 5000 });
      } else if (status === 400) {
        toast.error(errorDetail || 'Ungültige Eingabe. Bitte alle Pflichtfelder ausfüllen.', { duration: 5000 });
      } else if (status === 500) {
        toast.error('E-Mail Versand fehlgeschlagen. Bitte SMTP Konfiguration prüfen (IONOS) oder später erneut versuchen.', { duration: 6000 });
      } else {
        toast.error(`Fehler beim Einladen: ${errorDetail}`, { duration: 5000 });
      }
    } finally {
      setCreatingUser(false);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      await apiClient.patch(`/users/${userId}`, { is_active: !currentStatus });
      toast.success(`Benutzer ${!currentStatus ? 'aktiviert' : 'deaktiviert'}`);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Fehler beim Ändern des Status';
      toast.error(errorMessage);
    }
  };

  const handleCreateResetLink = async (userId) => {
    try {
      const response = await apiClient.post('/auth/admin-create-reset-link', { user_id: userId });
      const resetLink = response.data.reset_link;
      await navigator.clipboard.writeText(resetLink);
      toast.success('Reset-Link in Zwischenablage kopiert');
      toast.info(`Reset-Link: ${resetLink}`, { duration: 10000 });
    } catch (error) {
      console.error('Error creating reset link:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Fehler beim Erstellen des Reset-Links';
      toast.error(errorMessage);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setDeletingUser(true);
    try {
      await apiClient.delete(`/users/${userToDelete.id}`);
      toast.success(`Benutzer "${userToDelete.full_name}" wurde endgültig gelöscht.`);
      setDeleteUserDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Fehler beim Löschen des Benutzers';
      toast.error(errorMessage);
    } finally {
      setDeletingUser(false);
    }
  };

  const openDeleteUserDialog = (user) => {
    setUserToDelete(user);
    setDeleteUserDialogOpen(true);
  };

  return (
    <>
      <div className="flex justify-between items-center">
        <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Benutzerverwaltung</h2>
        <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
          <DialogTrigger asChild>
            <Button className="bg-sky-600 hover:bg-sky-500 text-white btn-glow border-none">
              <UserPlus className="w-4 h-4 mr-2" />
              Nutzer einladen
            </Button>
          </DialogTrigger>
          <DialogContent className={isDark 
            ? "bg-slate-900 border-slate-700 text-white sm:max-w-[500px]" 
            : "bg-white border-slate-300 text-slate-900 sm:max-w-[500px]"
          }>
            <DialogHeader>
              <DialogTitle>Nutzer einladen</DialogTitle>
              <DialogDescription className={isDark ? "text-slate-300" : "text-slate-600"}>
                Der Nutzer erhält eine Einladungs-E-Mail mit einem Link zum Setzen des Passworts.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleInviteUser} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input 
                  value={createUserForm.full_name} 
                  onChange={e => setCreateUserForm({...createUserForm, full_name: e.target.value})} 
                  className={isDark ? "bg-slate-950 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"} 
                  required 
                />
              </div>
              <div className="space-y-2">
                <Label>E-Mail</Label>
                <Input 
                  type="email"
                  value={createUserForm.email} 
                  onChange={e => setCreateUserForm({...createUserForm, email: e.target.value})} 
                  className={isDark ? "bg-slate-950 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"} 
                  required 
                />
              </div>
              <div className="space-y-2">
                <Label>Rolle</Label>
                <Select value={createUserForm.role} onValueChange={v => setCreateUserForm({...createUserForm, role: v})}>
                  <SelectTrigger className={isDark ? "bg-slate-950 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"}>
                    <SelectItem value="Employee">Mitarbeiter (Upload & View)</SelectItem>
                    <SelectItem value="Citizen">Bürger (Read only)</SelectItem>
                    {userInfo?.role === 'ProviderAdmin' && (
                      <SelectItem value="MunicipalityAdmin">Gemeinde-Admin</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              {userInfo?.role === 'ProviderAdmin' && (
                <div className="space-y-2">
                  <Label>
                    Gemeinde
                    {createUserForm.role !== 'ProviderAdmin' && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  <Select value={createUserForm.municipality_id} onValueChange={v => setCreateUserForm({...createUserForm, municipality_id: v})}>
                    <SelectTrigger className={isDark ? "bg-slate-950 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"}>
                      <SelectValue placeholder="Gemeinde auswählen..." />
                    </SelectTrigger>
                    <SelectContent className={isDark ? "bg-slate-900 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"}>
                      {createUserForm.role === 'ProviderAdmin' && (
                        <SelectItem value="">Keine (Provider-Level)</SelectItem>
                      )}
                      {municipalitiesList.map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.name || m.id}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {createUserForm.role !== 'ProviderAdmin' && (
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      Erforderlich für Mitarbeiter und Gemeinde-Admins
                    </p>
                  )}
                </div>
              )}
              <div className="flex flex-col gap-2 pt-2">
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Sendet eine Einladung mit initialem Passwort-Link per E-Mail.
                </p>
                <div className="flex justify-end">
                  <Button type="submit" disabled={creatingUser} className="bg-sky-600 hover:bg-sky-500 text-white">
                    {creatingUser ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {creatingUser ? 'Wird gesendet...' : 'Einladung senden'}
                  </Button>
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className={isDark 
        ? "bg-slate-900 border-slate-800 shadow-lg" 
        : "bg-slate-50 border-slate-200 shadow-lg"
      }>
        <CardContent className="p-0">
          <Table>
            <TableHeader className={isDark ? "bg-slate-950" : "bg-slate-50"}>
              <TableRow className={isDark ? "border-slate-800 hover:bg-slate-950" : "border-slate-200 hover:bg-slate-50"}>
                <TableHead className={isDark ? "text-slate-200" : "text-slate-600"}>Name</TableHead>
                <TableHead className={isDark ? "text-slate-200" : "text-slate-600"}>Rolle</TableHead>
                <TableHead className={isDark ? "text-slate-200" : "text-slate-600"}>Gemeinde</TableHead>
                <TableHead className={isDark ? "text-slate-200" : "text-slate-600"}>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadingUsers ? (
                <TableRow>
                  <TableCell colSpan={4} className={`h-32 text-center ${isDark ? "text-slate-500" : "text-slate-600"}`}>
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Lade Benutzer...
                  </TableCell>
                </TableRow>
              ) : users.filter(u => !selectedMunicipalityId || u.municipality_id === selectedMunicipalityId).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className={`h-32 text-center ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                    Keine Benutzer gefunden.
                  </TableCell>
                </TableRow>
              ) : (
                users
                  .filter(u => !selectedMunicipalityId || u.municipality_id === selectedMunicipalityId)
                  .map((user) => (
                  <TableRow key={user.id} className={isDark 
                    ? "border-slate-800 hover:bg-slate-800/50 transition-colors" 
                    : "border-slate-200 hover:bg-slate-50 transition-colors"
                  }>
                    <TableCell className={`font-medium ${isDark ? "text-slate-100" : "text-slate-900"}`}>
                      <div className="flex flex-col">
                        <span>{user.full_name}</span>
                        <span className={`text-xs ${isDark ? "text-slate-300" : "text-slate-600"}`}>{user.email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={isDark ? "border-slate-500 text-slate-200" : "border-slate-400 text-slate-700"}>
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell className={`text-sm font-mono text-xs ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                      {user.municipality_id || '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant={user.is_active ? "default" : "destructive"} className={user.is_active ? "bg-green-500/10 text-green-500 hover:bg-green-500/20 border-0" : ""}>
                          {user.is_active ? 'Aktiv' : 'Inaktiv'}
                        </Badge>
                        {(userInfo?.role === 'ProviderAdmin' || 
                          (userInfo?.role === 'MunicipalityAdmin' && user.role !== 'ProviderAdmin' && user.municipality_id === userInfo?.municipality_id)) && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCreateResetLink(user.id)}
                              title="Passwort-Reset-Link erstellen"
                              className={isDark 
                                ? "hover:bg-slate-700 text-slate-300 hover:text-white h-7 px-2" 
                                : "hover:bg-slate-100 text-slate-600 hover:text-slate-900 h-7 px-2"
                              }
                            >
                              <Lock className="w-3.5 h-3.5 text-blue-400" />
                            </Button>
                            {user.id !== userInfo?.sub && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                                  title={user.is_active ? 'Deaktivieren' : 'Aktivieren'}
                                  className={isDark 
                                    ? "hover:bg-slate-700 text-slate-300 hover:text-white h-7 px-2" 
                                    : "hover:bg-slate-100 text-slate-600 hover:text-slate-900 h-7 px-2"
                                  }
                                >
                                  {user.is_active ? (
                                    <PowerOff className="w-3.5 h-3.5 text-orange-400" />
                                  ) : (
                                    <Power className="w-3.5 h-3.5 text-green-400" />
                                  )}
                                </Button>
                                {userInfo?.role === 'ProviderAdmin' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openDeleteUserDialog(user)}
                                    title="Benutzer löschen"
                                    className={isDark 
                                      ? "hover:bg-red-900/30 text-slate-300 hover:text-red-400 h-7 px-2" 
                                      : "hover:bg-red-50 text-slate-600 hover:text-red-600 h-7 px-2"
                                    }
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* DELETE USER CONFIRMATION DIALOG */}
      <AlertDialog open={deleteUserDialogOpen} onOpenChange={setDeleteUserDialogOpen}>
        <AlertDialogContent className={isDark 
          ? "bg-slate-900 border-slate-700 text-white" 
          : "bg-white border-slate-300 text-slate-900"
        }>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Benutzer endgültig löschen?</AlertDialogTitle>
            <AlertDialogDescription className={isDark ? "text-slate-300" : "text-slate-600"}>
              Möchten Sie den Benutzer <strong>{userToDelete?.full_name}</strong> ({userToDelete?.email}) wirklich <strong className="text-red-600">endgültig löschen</strong>?
              <br /><br />
              <span className="text-red-600 font-medium">⚠️ Diese Aktion kann nicht rückgängig gemacht werden!</span>
              <br />
              Der Benutzer wird vollständig aus dem System entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              disabled={deletingUser}
              className={isDark ? "bg-slate-800 border-slate-600 text-white hover:bg-slate-700" : ""}
            >
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={deletingUser}
              className="bg-red-600 hover:bg-red-500 text-white"
            >
              {deletingUser ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {deletingUser ? 'Wird gelöscht...' : 'Löschen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
